import { useMemo } from 'react';
import dayjs from 'dayjs';

import {type ILead, useLeads} from 'src/services/api/leads';
import {useProperties} from 'src/services/api/properties/use-properties';
import {useShowings} from 'src/services/api/showings';
import {type IProperty} from 'src/services/api/properties/types';
import {type IShowing} from 'src/api/landlord-showings-api';

export enum PossiblyFilledReason {
    LessThan3LeadsLastTwoWeeks = 'LessThan3LeadsLastTwoWeeks',
    CreatedTwoWeeksAgoAndHasNoLeads = 'CreatedTwoWeeksAgoAndHasNoLeads',
    NoShowingsInLastMonth = 'NoShowingsInLastMonth',
}

export const getPossiblyFilledReasonLabel = (reason: PossiblyFilledReason | undefined): string | null => {
    switch (reason) {
        case PossiblyFilledReason.LessThan3LeadsLastTwoWeeks:
            return 'Fewer than 3 leads in the last 2 weeks';
        case PossiblyFilledReason.CreatedTwoWeeksAgoAndHasNoLeads:
            return 'Created over 2 weeks ago and has no leads';
        case PossiblyFilledReason.NoShowingsInLastMonth:
            return 'No showings in the last month';
        default:
            return null;
    }
};

export interface IVacantProperty extends IProperty {
    isPossiblyFilled: boolean;
    possiblyFilledFirstReason?: PossiblyFilledReason;
}

interface IVacantPropertiesData {
    properties: IVacantProperty[];
    possiblyFilledPropertiesAmount: number;
    isLoading: boolean;
}

const now = dayjs.utc();

const getFirstEligibleReason = (p: IProperty, leads: ILead[], showings: IShowing[]): PossiblyFilledReason | undefined => {
    if (!p.is_vacant || p.type !== 'Condo') {
        return undefined;
    }

    if (p.vacant_until && dayjs.utc(p.vacant_until).isAfter(now)) {
        return undefined;
    }

    // check LessThan3LeadsLastTwoWeeks
    const twoWeeksAgo = dayjs.utc().subtract(14, 'day');

    const leadsLastTwoWeeks = leads.filter((lead) => {
        const leadCreatedAt = dayjs.utc(lead.inserted_at);
        return lead.property_id === p.id && leadCreatedAt.isAfter(twoWeeksAgo);
    });

    if (leadsLastTwoWeeks.length < 3) {
        return PossiblyFilledReason.LessThan3LeadsLastTwoWeeks;
    }

    // check CreatedTwoWeeksAgoAndHasNoLeads
    const leadsWithProperty = leads.filter((lead) => lead.property_id === p.id);

    if (leadsWithProperty.length === 0 && dayjs.utc(p.created_at).isBefore(twoWeeksAgo)) {
        return PossiblyFilledReason.CreatedTwoWeeksAgoAndHasNoLeads;
    }


    // check NoShowingsInLastMonth
    const oneMonthAgo = dayjs.utc().subtract(1, 'month');

    const hasAtLeastOneShowingLastMonth = showings.some((showing) => {
        const showingDate = dayjs.utc(showing.start_time);
        return showing.property_id === p.id && showingDate.isAfter(oneMonthAgo);
    });

    if (!hasAtLeastOneShowingLastMonth) {
        return PossiblyFilledReason.NoShowingsInLastMonth;
    }

    return undefined;
};

export const useVacantProperties = (): IVacantPropertiesData => {
    const { data: _properties, isLoading: isPropertiesLoading } = useProperties();
    const { data: _leads, isLoading: isLeadsLoading } = useLeads();
    const { data: _showings, isLoading: isShowingsLoading } = useShowings({
        whole_team: true,
    });

    const isLoading = isPropertiesLoading || isLeadsLoading || isShowingsLoading;

    return useMemo(() => {
        if (!_properties || !_leads || !_showings || isLoading) {
            return {
                properties: [],
                possiblyFilledPropertiesAmount: 0,
                isLoading,
            };
        }

        let possiblyFilledPropertiesAmount = 0;

        const properties = _properties.map((p) => {
            const possibleReason = getFirstEligibleReason(p, _leads, _showings);
            const isPossiblyFilled = !!possibleReason;
            if (isPossiblyFilled) {
                possiblyFilledPropertiesAmount++;
            }
            return {
                ...p,
                isPossiblyFilled,
                possiblyFilledFirstReason: possibleReason,
            };
        });

        return {
            properties,
            possiblyFilledPropertiesAmount,
            isLoading,
        };
    }, [_leads, _properties, _showings, isLoading]);
};
